import { get } from "svelte/store"
import type { ServiceHoursConfig } from "@kontextwerk/control-web-client"
import { apiBaseOverride } from "../../../_shared/_logic/store"
import { baseAPIURL } from "../../_shared/_logic/config"
import { parseApiErrorPayload, resolveApiErrorMessage } from "../../error-message"

export class ApiError extends Error {
  status: number
  payload: unknown

  constructor(status: number, message: string, payload?: unknown) {
    super(message)
    this.status = status
    this.payload = payload
  }
}

export type OrganizationAddress = {
  street: string | null
  house_number: string | null
  zip: string | null
  city: string | null
  country: string | null
  address_extra: string | null
}

export type OrganizationBranding = {
  symbol_bright: string
  symbol_dark: string
  logo_bright: string
  logo_dark: string
}

export type PublicWidgetConfig = {
  organization_id?: string | null
  project_id: string
  project_slug: string
  project_name?: string | null
  realization_id: string
  realization_slug?: string | null
  realization_name?: string | null
  api_base?: string | null
  cdn_base?: string | null
  transcription_stream?: string | null
  widget_config?: Record<string, unknown> | null
}

export type OrganizationPresentation = {
  id: string
  name: string
  slug: string
  public_name?: string | null
  data_privacy_url?: string | null
  tier?: string | null
  plan_family_id?: string | null
  description: string | null
  contact_email?: string | null
  contact_phone?: string | null
  presentation_indexing_enabled?: boolean
  address?: OrganizationAddress | null
  service_hours?: ServiceHoursConfig | null
  branding?: OrganizationBranding | null
  widget_project_name?: string | null
  widget_realization_id?: string | null
}

const BASE_PATH = "/public/organization"
const organizationPresentationRequests = new Map<string, Promise<OrganizationPresentation>>()
const widgetConfigRequests = new Map<string, Promise<PublicWidgetConfig>>()

const dedupeInFlight = <T>(
  requests: Map<string, Promise<T>>,
  key: string,
  factory: () => Promise<T>,
): Promise<T> => {
  const existing = requests.get(key)

  if (existing) return existing

  const request = factory().finally(() => {
    requests.delete(key)
  })
  requests.set(key, request)

  return request
}

const seedWidgetConfigPreload = (payload: unknown): void => {
  if (typeof window === "undefined") return

  if (!payload || typeof payload !== "object") return

  const data = payload as Record<string, unknown>
  const project =
    typeof data.project_name === "string"
      ? data.project_name.trim()
      : typeof data.project_slug === "string"
        ? data.project_slug.trim()
        : ""

  if (!project) return

  const realizationRaw = typeof data.realization_id === "string" ? data.realization_id.trim() : ""
  const normalizedRealization = realizationRaw ? realizationRaw.toLowerCase() : "default"
  const organizationRaw =
    typeof data.organization_id === "string" ? data.organization_id.trim() : ""
  const normalizedOrganization = organizationRaw ? organizationRaw.toLowerCase() : "global"

  const adminKey = typeof data.admin_key === "string" ? data.admin_key.trim() : ""

  const normalizedProject = project.toLowerCase()
  const cacheKey = adminKey
    ? `${normalizedProject}::${normalizedOrganization}::${normalizedRealization}::admin::${adminKey}`
    : `${normalizedProject}::${normalizedOrganization}::${normalizedRealization}::anon`

  const globalAny = window as typeof window & {
    __kontextwerkWidgetConfigPreload?: Record<string, unknown>
  }
  globalAny.__kontextwerkWidgetConfigPreload = globalAny.__kontextwerkWidgetConfigPreload ?? {}
  globalAny.__kontextwerkWidgetConfigPreload[cacheKey] = payload
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0

const ensureObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const hasExplicitWidgetLogo = (widgetConfig: Record<string, unknown>): boolean => {
  const brand = ensureObject(widgetConfig.brand)
  const logo = ensureObject(brand?.logo)

  if (isNonEmptyString(logo?.url)) return true

  const custom = ensureObject(widgetConfig.custom)

  return isNonEmptyString(custom?.widget_logo)
}

const withOrganizationBrandingFallback = (
  payload: PublicWidgetConfig,
  branding: OrganizationBranding | null | undefined,
): PublicWidgetConfig => {
  const logoUrl =
    branding?.symbol_bright?.trim() ||
    branding?.symbol_dark?.trim() ||
    branding?.logo_bright?.trim() ||
    branding?.logo_dark?.trim() ||
    ""

  if (!logoUrl) return payload

  const widgetConfig = ensureObject(payload.widget_config)

  if (!widgetConfig || hasExplicitWidgetLogo(widgetConfig)) return payload

  return {
    ...payload,
    widget_config: {
      ...widgetConfig,
      brand: {
        ...(ensureObject(widgetConfig.brand) ?? {}),
        logo: {
          ...(ensureObject(ensureObject(widgetConfig.brand)?.logo) ?? {}),
          url: logoUrl,
        },
      },
    },
  }
}

const resolveBaseUrl = () => {
  const override = get(apiBaseOverride)

  return override ?? baseAPIURL
}

const joinUrl = (path: string) => {
  const base = resolveBaseUrl().replace(/\/+$/, "")
  const normalized = path.startsWith("/") ? path : `/${path}`

  return `${base}${normalized}`
}

export const fetchOrganizationPresentation = async (orgIdentifier: string) => {
  const target = orgIdentifier?.trim()

  if (!target) {
    throw new ApiError(400, "Ungültige Organisation.")
  }

  const url = joinUrl(`${BASE_PATH}/${encodeURIComponent(target)}`)

  return dedupeInFlight(organizationPresentationRequests, url, async () => {
    const response = await fetch(url)

    if (!response.ok) {
      const { detail } = await parseApiErrorPayload(response)
      const message = resolveApiErrorMessage(detail, {
        statusCode: response.status,
        statusText: response.statusText,
      })
      throw new ApiError(response.status, message, detail)
    }

    return (await response.json()) as OrganizationPresentation
  })
}

const buildWidgetConfigUrl = ({
  projectName,
  organizationId,
  realizationId,
}: {
  projectName: string
  organizationId?: string | null
  realizationId?: string | null
}) => {
  const normalizedProjectName = projectName?.trim()

  if (!normalizedProjectName) {
    throw new ApiError(400, "project_name erforderlich.")
  }

  const params = new URLSearchParams()
  params.set("project_name", normalizedProjectName)
  const normalizedOrganizationId = organizationId?.trim()

  if (normalizedOrganizationId) params.set("organization_id", normalizedOrganizationId)

  const normalizedRealizationId = realizationId?.trim()

  if (normalizedRealizationId) params.set("realization_id", normalizedRealizationId)

  return joinUrl(`/public/widget/config?${params.toString()}`)
}

export const fetchWidgetConfig = async (params: {
  projectName: string
  organizationId?: string | null
  realizationId?: string | null
  branding?: OrganizationBranding | null
}): Promise<PublicWidgetConfig> => {
  const url = buildWidgetConfigUrl(params)

  return dedupeInFlight(widgetConfigRequests, url, async () => {
    const response = await fetch(url)

    if (!response.ok) {
      const { detail } = await parseApiErrorPayload(response)
      const message = resolveApiErrorMessage(detail, {
        statusCode: response.status,
        statusText: response.statusText,
      })
      throw new ApiError(response.status, message, detail)
    }

    const payload = withOrganizationBrandingFallback(
      (await response.json()) as PublicWidgetConfig,
      params.branding,
    )
    seedWidgetConfigPreload(payload)

    return payload
  })
}

export const __testing = {
  clearPresentationRequestDedupe: () => {
    organizationPresentationRequests.clear()
    widgetConfigRequests.clear()
  },
}
